import { useState } from 'react'
import { LANGUAGES, useT } from '@/i18n'
import type { DisplayMode } from '@/lib/board'
import { formatPrice } from '@/lib/keno-view'
import {
  BellIcon, CloseIcon, GLI_CERT_URL, HamburgerIcon, KenoLogo, ResultIcon, RulesIcon, SpeakerIcon,
  StatementIcon, UserIcon, WalletIcon,
} from './Icons'

interface HeaderProps {
  username: string
  balance: number | null
  checking: boolean
  onCheckBalance: () => void
  displayMode: DisplayMode
  onDisplayMode: (m: DisplayMode) => void
  langId: number
  onLang: (n: number) => void
  soundOn: boolean
  onToggleSound: () => void
  openWindow: (path: string) => void
  onLogout: () => void
}

/**
 * The reference links carry an href (javascript:void(0)); without one, bootstrap's
 * a:not([href]) rule recolours them. "#" keeps the styling, the handler cancels it.
 */
const link = (fn: () => void) => ({ href: '#', onClick: (e: { preventDefault: () => void }) => { e.preventDefault(); fn() } })

/** The reference top bar, mobile bar + drawer, and the mobile view-mode radios. */
export function Header(p: HeaderProps) {
  const t = useT()
  const [drawer, setDrawer] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const balanceText = p.balance !== null ? `THB ${formatPrice(p.balance)}` : null

  return (
    <>
      <div className="topnav-cont">
        <div className="left-menu">
          <div className="menu-item"><KenoLogo /></div>
          <div className="menu-item"><a href={GLI_CERT_URL} target="_blank" rel="noopener noreferrer"><img src="/assets/img/gli-certified.png" style={{ height: 20 }} /></a></div>
          <div className="menu-item">
            <div className="desktop-varcont menu-toggle-item" onClick={p.onCheckBalance}>
              <WalletIcon />{' '}
              {balanceText !== null ? <span>{balanceText} {p.checking && <div className="loading-icon" />}</span> : <span>0.00</span>}
              <div className="toggle-tooltip">{t('WALLET')}</div>
            </div>
          </div>
          <div className="menu-item">
            <div className="menu-dropdown">
              {p.displayMode === 'normal' && t('menunormal')}
              {p.displayMode === 'simple' && t('menusimple')}
              {p.displayMode === 'trend' && t('menutrend')}
              <span />
              <div className="dropdown-list">
                <div className="dropdown-item" onClick={() => p.onDisplayMode('normal')}>{t('menunormal')}</div>
                <div className="dropdown-item" onClick={() => p.onDisplayMode('simple')}>{t('menusimple')}</div>
                <div className="dropdown-item menutrend" onClick={() => p.onDisplayMode('trend')}>{t('menutrend')}</div>
              </div>
            </div>
          </div>
          <div className="menu-item">
            <div className="menu-dropdown">
              {' '}{LANGUAGES[p.langId]}
              <span />
              <div className="dropdown-list" style={{ minWidth: 130 }}>
                {LANGUAGES.map((l, i) => <div key={l} className="dropdown-item" onClick={() => p.onLang(i)}>{l}</div>)}
              </div>
            </div>
          </div>
        </div>
        <div className="right-menu">
          <div className="menu-item">
            <div className="menu-dropdown">
              {' '}{p.username}
              <span />
              <div className="dropdown-list">
                <a className="dropdown-item" {...link(p.onLogout)}>{t('menulogout')}</a>
              </div>
            </div>
          </div>
          <div className="menu-item">
            <div className={`menu-toggle-item ${p.soundOn ? 'soundon' : 'soundoff'}`} onClick={p.onToggleSound}>
              <span><SpeakerIcon /></span>
              <div className="toggle-tooltip">{t('menusfx')}: {p.soundOn ? t('menuon') : t('menuoff')}</div>
            </div>
          </div>
          <div className="menu-item">
            <div className="menu-toggle-item" onClick={() => p.openWindow('/rules')}>
              <span><RulesIcon /></span>
              <div className="toggle-tooltip">{t('menurules')}</div>
            </div>
          </div>
          <div className="menu-item">
            <div className="menu-toggle-item" onClick={() => p.openWindow('/results')}>
              <span><ResultIcon /></span>
              <div className="toggle-tooltip">{t('menuresult')}</div>
            </div>
          </div>
          <div className="menu-item">
            <div className="menu-toggle-item" onClick={() => p.openWindow('/statement')}>
              <span><StatementIcon /></span>
              <div className="toggle-tooltip">{t('menustatement')}</div>
            </div>
          </div>
          <div className="menu-item">
            <div className="menu-toggle-item" onClick={() => p.openWindow('/announcement')}>
              <span><BellIcon /></span>
              <div className="toggle-tooltip">{t('menunotification')}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="header-mobile">
        <div className="left-nav">
          <span onClick={() => setDrawer((d) => !d)}><HamburgerIcon /></span>
        </div>
        <div className="center-nav">
          <div className="mobile-varcont" onClick={p.onCheckBalance}>
            <WalletIcon />{' '}
            {balanceText !== null ? <span>{balanceText} {p.checking && <div className="loading-icon" />}</span> : <span>&nbsp;</span>}
          </div>
          <div className="mobile-varcont username">
            <UserIcon />
            {' '}{p.username}
          </div>
        </div>
        <div className={`mobile-left-drawer ${drawer ? 'open' : ''}`}>
          {drawer && <div className="mobile-drawer-bg" onClick={() => setDrawer(false)} />}
          <div className="mobile-drawer">
            <div className="mobile-drawer-rela">
              <a className="mobile-drawer-close" {...link(() => setDrawer(false))}><CloseIcon /></a>
              <div className="game-logo">
                {/* The reference nests these two links; a browser parses that into siblings. */}
                <a {...link(() => {})}><KenoLogo /> </a>
                <a href={GLI_CERT_URL} target="_blank" rel="noopener noreferrer"><img src="/assets/img/gli-certified.png" style={{ height: 20, marginLeft: 12 }} /></a>
              </div>
              <a className="drawer-link" {...link(() => p.openWindow('/results'))}><span><ResultIcon /></span> {t('menuresult')}</a>
              <a className="drawer-link" {...link(() => p.openWindow('/statement'))}><span><StatementIcon /></span> {t('menustatement')} </a>
              <a className="drawer-link" {...link(() => p.openWindow('/rules'))}><span><RulesIcon /></span> {t('menurules')}</a>
              <a className="drawer-link" {...link(() => p.openWindow('/announcement'))}><span><BellIcon /></span> {t('menunotification')}</a>
              <div className={`multiplechoices ${langOpen ? 'open' : ''}`} onClick={() => setLangOpen((o) => !o)}>
                <div className="selected">{LANGUAGES[p.langId]}</div>
                <div className="choices">
                  {LANGUAGES.map((l, i) => <div key={l} className="choice" onClick={() => p.onLang(i)}>{l}</div>)}
                </div>
              </div>
            </div>
            <div className="bottom">
              <div style={{ padding: 10, width: '100%' }}>
                <a className={`drawer-toggle ${p.soundOn ? 'soundon' : 'soundoff'}`} {...link(p.onToggleSound)}>
                  <span><SpeakerIcon /></span> {t('menusfx')}: {p.soundOn ? t('menuon') : t('menuoff')}
                </a>
              </div>
              <a {...link(p.onLogout)} className="drawerlogout">
                {t('menulogout')}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="filter-options">
        {(['normal', 'simple', 'trend'] as DisplayMode[]).map((m) => (
          <label key={m} className={m === 'trend' ? 'menutrend' : undefined}>
            <input type="radio" checked={p.displayMode === m} onChange={() => p.onDisplayMode(m)} value={m} /><em />
            {t(m === 'normal' ? 'menunormal' : m === 'simple' ? 'menusimple' : 'menutrend')}
          </label>
        ))}
      </div>
    </>
  )
}
